import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, UserPlus, AlertTriangle } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const blank = { name: "", phone: "", email: "", city: "", business_name: "", source: "Manual", owner_user_id: "" };

export default function QuickAddLead({ open, onOpenChange, onCreated, users = [] }) {
  const { user, options } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(blank);
  const [loading, setLoading] = useState(false);
  const [dup, setDup] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const isManager = user?.role === "Admin" || user?.role === "Sales Manager";
  const sources = options?.lead_sources || ["Manual", "Referral", "WhatsApp", "Instagram", "Website", "Existing client reference", "Other"];
  const assignables = (users || []).filter((u) => u.role !== "Admin" && u.status !== "Inactive");

  const reset = () => { setForm(blank); setDup(null); };

  const create = async (force = false) => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    if (!form.phone.trim() && !form.email.trim()) { toast.error("Add a phone or email"); return; }
    setLoading(true);
    try {
      const payload = { ...form, owner_user_id: form.owner_user_id || undefined, force_create: force };
      const { data } = await api.post("/leads", payload);
      const d = data.data;
      if (d.duplicate) { setDup(d.duplicate); return; }
      const lead = d;
      toast.success(`Lead created${lead?.owner_name ? ` · assigned to ${lead.owner_name}` : ""}`);
      reset();
      onCreated?.(lead);
      onOpenChange(false);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const fields = [
    ["name", "Full name *", "text"],
    ["phone", "Phone", "tel"],
    ["email", "Email", "email"],
    ["business_name", "Business / company", "text"],
    ["city", "City", "text"],
  ];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="border-sky bg-white sm:max-w-md" data-testid="quick-add-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-sora text-navy">
            <UserPlus className="h-5 w-5 text-loop" /> Quick Add Lead
          </DialogTitle>
        </DialogHeader>

        {dup ? (
          <div className="space-y-4" data-testid="duplicate-dialog">
            <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50/60 p-4">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
              <p className="text-sm text-ink/80" data-testid="duplicate-message">{dup.message}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {dup.can_open && dup.lead_id && (
                <button data-testid="duplicate-open" onClick={() => { onOpenChange(false); navigate(`/leads?open=${dup.lead_id}`); }} className="flex-1 rounded-xl bg-loop py-2.5 text-sm font-semibold text-white hover:bg-loophover">Open existing lead</button>
              )}
              {dup.can_override && (
                <button data-testid="duplicate-override" onClick={() => create(true)} disabled={loading} className="rounded-xl border border-red-200 px-3 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-60">Create anyway</button>
              )}
              <button onClick={() => setDup(null)} className="rounded-xl border border-sky px-3 py-2.5 text-sm font-medium text-ink/70 hover:bg-mist">Back</button>
            </div>
          </div>
        ) : (
          <form onSubmit={(e) => { e.preventDefault(); create(false); }} className="space-y-3">
            {fields.map(([key, label, type]) => (
              <div key={key}>
                <label className="mb-1 block text-sm font-medium text-ink/80">{label}</label>
                <input data-testid={`quick-add-${key}`} type={type} value={form[key]} onChange={(e) => set(key, e.target.value)}
                  className="h-11 w-full rounded-xl border border-sky px-3.5 text-sm outline-none focus:border-loop focus:ring-2 focus:ring-loop/20" />
              </div>
            ))}
            <div>
              <label className="mb-1 block text-sm font-medium text-ink/80">Source</label>
              <select data-testid="quick-add-source" value={form.source} onChange={(e) => set("source", e.target.value)}
                className="h-11 w-full rounded-xl border border-sky bg-white px-3 text-sm outline-none focus:border-loop">
                {sources.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            {isManager && (
              <div>
                <label className="mb-1 block text-sm font-medium text-ink/80">Assign to</label>
                <select data-testid="quick-add-owner" value={form.owner_user_id} onChange={(e) => set("owner_user_id", e.target.value)}
                  className="h-11 w-full rounded-xl border border-sky bg-white px-3 text-sm outline-none focus:border-loop">
                  <option value="">Auto (assignment rules / unassigned)</option>
                  {assignables.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
            )}
            <button type="submit" disabled={loading} data-testid="quick-add-submit"
              className="flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-loop font-sora text-sm font-semibold text-white hover:bg-loophover disabled:opacity-60">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Create lead"}
            </button>
            <p className="text-center text-xs text-ink/50">
              {user?.role === "Sales Executive" ? "This lead will be assigned to you." : "Choose an owner, or let the assignment engine route it."}
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
